import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Ban, Pencil, Tags, Trash2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { StatusBadge, useWh } from "@/lib/wh";
import { ItemForm } from "@/components/wh/ItemForm";
import { ItemCodes } from "@/components/wh/ItemCodes";
import { ItemPhotos, ItemInfo, ItemOps } from "@/components/wh/ItemDetails";
import { MovementList } from "@/components/wh/MovementList";
import { SellRemoveDialog } from "@/components/wh/SellRemoveDialog";
import { LabelDialog } from "@/components/wh/LabelDialog";

function TransferSelect({ it, lots, onDone }) {
  const move = async (e) => {
    const lot_id = e.target.value === "none" ? null : e.target.value;
    try { onDone((await api.post(`/wh/items/${it.id}/transfer`, { lot_id })).data); toast.success("Articolo trasferito"); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <label className="btn-secondary h-11 px-3 text-sm relative" data-testid="wh-transfer">
      <ArrowRightLeft className="h-4 w-4" /> Trasferisci
      <select value="" onChange={move} className="absolute inset-0 opacity-0 cursor-pointer" aria-label="Trasferisci in un altro lotto" data-testid="wh-transfer-select">
        <option value="" disabled>Scegli lotto</option>
        {it.lot_id && <option value="none">Senza lotto</option>}
        {lots.filter((l) => l.id !== it.lot_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </label>
  );
}

export default function WhItem() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { wh, setWh } = useWh();
  const isNew = id === "nuovo";
  const [it, setIt] = useState(null);
  const [lots, setLots] = useState([]);
  const [moves, setMoves] = useState([]);
  const [edit, setEdit] = useState(params.get("edit") === "1");
  const [mode, setMode] = useState(null);
  const [labels, setLabels] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const [a, m] = await Promise.all([api.get(`/wh/items/${id}`), api.get("/wh/movements", { params: { item_id: id } })]);
      setIt(a.data);
      setMoves(m.data);
    } catch (e) { setErr(formatApiError(e)); }
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);
  const itemWh = it?.wh || wh;
  useEffect(() => { if (it?.wh && it.wh !== wh) setWh(it.wh); }, [it?.wh]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/wh/lots", { params: { wh: itemWh } }).then(({ data }) => setLots(data)).catch(() => {}); }, [itemWh]);
  const changed = (data) => { setIt(data); api.get("/wh/movements", { params: { item_id: id } }).then(({ data: m }) => setMoves(m)); };
  const restore = async (o) => {
    if (!window.confirm("Annullare questa operazione? Le unità tornano disponibili e resta traccia nello storico.")) return;
    try { changed((await api.post(`/wh/items/${id}/restore/${o.kind}/${o.id}`)).data); toast.success("Operazione annullata"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    if (window.prompt(`Eliminare definitivamente ${it.code}? Lo storico dei movimenti resta. Scrivi ELIMINA per confermare.`) !== "ELIMINA") return;
    try { await api.delete(`/wh/items/${id}`); toast.success("Articolo eliminato"); nav("/magazzino"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const back = <Link to={it?.lot_id ? `/magazzino/lotti/${it.lot_id}` : "/magazzino"} className="text-sm text-[var(--muted)] flex items-center gap-1" data-testid="wh-back"><ArrowLeft className="h-4 w-4" /> {it?.lot_name || "Magazzino"}</Link>;
  if (isNew) {
    return (
      <div className="space-y-6 max-w-3xl">{back}<h1 className="font-heading text-3xl font-bold tracking-tight">Nuovo articolo</h1>
        <ItemForm wh={wh} lots={lots.filter((l) => !l.archived)} lotId={params.get("lot")} onSaved={(d) => nav(`/magazzino/articoli/${d.id}`, { replace: true })} onCancel={() => nav(-1)} /></div>
    );
  }
  if (err) return <div className="space-y-3" data-testid="wh-item-error"><p>{err}</p><Link to="/magazzino" className="underline">Torna al magazzino</Link></div>;
  if (!it) return <p className="text-[var(--muted)]">Caricamento...</p>;
  if (edit) return <div className="space-y-6 max-w-3xl">{back}<h1 className="font-heading text-3xl font-bold tracking-tight">Modifica {it.code}</h1><ItemForm item={it} lots={lots} onSaved={(d) => { changed(d); setEdit(false); }} onCancel={() => setEdit(false)} /></div>;
  const w = can(user, "wh_write");
  return (
    <div className="space-y-6">
      {back}
      <div className="flex flex-wrap items-center gap-3 fade-up">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight font-mono" data-testid="wh-item-title">{it.code}</h1><StatusBadge status={it.status} />
      </div>
      {it.description && <p className="text-lg -mt-3 break-words">{it.description}</p>}
      <div className="flex flex-wrap gap-2" data-testid="wh-item-actions">
        {w && <button onClick={() => setMode("sell")} disabled={!it.qty_available} className="btn-primary h-11 px-4" data-testid="wh-item-sell"><Check className="h-5 w-5" /> V – Venduto</button>}
        {w && <button onClick={() => setMode("remove")} disabled={!it.qty_available} className="btn-secondary h-11 px-4 text-red-600" data-testid="wh-item-remove"><Ban className="h-5 w-5" /> Rimosso</button>}
        {w && <button onClick={() => setEdit(true)} className="btn-secondary h-11 px-4" data-testid="wh-item-edit"><Pencil className="h-4 w-4" /> Modifica</button>}
        {w && <TransferSelect it={it} lots={lots} onDone={changed} />}
        <button onClick={() => setLabels(true)} className="btn-secondary h-11 px-4" data-testid="wh-item-label"><Tags className="h-4 w-4" /> Etichetta</button>
        {user.role === "admin" && <button onClick={remove} className="btn-ghost text-red-600" aria-label="Elimina definitivamente" data-testid="wh-item-delete"><Trash2 className="h-5 w-5" /></button>}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6"><ItemPhotos photos={it.photos} /><ItemCodes item={it} /></div>
        <div className="space-y-6"><ItemInfo it={it} /><ItemOps it={it} canRestore={can(user, "wh_manage")} onRestore={restore} /></div>
      </div>
      <section className="space-y-3"><h2 className="font-heading text-lg font-semibold">Storico dell'articolo</h2><MovementList rows={moves} /></section>
      {mode && <SellRemoveDialog item={it} mode={mode} onClose={() => setMode(null)} onDone={changed} />}
      <LabelDialog open={labels} onOpenChange={setLabels} ids={[it.id]} wh={it.wh} />
    </div>
  );
}
